import _ from "lodash";
import React from "react";
import styled from "styled-components";
import { Checkbox, Label, Popup, Icon, Placeholder, Button } from "semantic-ui-react";
import { DateBadge, DateTimeBadge, DateDueBadge } from "../badges";
import AppSettings from "../../config";

const TopicPlusHeader = styled.p`
  color: #f26c21;
  font-size: 14px;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: no-wrap;
  max-width: 100%;
`;
const TopicPlusLocation = styled.p`
  color: #00000099;
  font-size: 12px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: no-wrap;
`;
const TopicPlusDescription = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 3px;
  white-space: pre-wrap;
  line-height: 1.1em;
  font-size: 12px;
`;

export const DesktopColumns = (
  site: string,
  crmRequestImages: { [key: number]: string | undefined },
  selectAll: boolean,
  setSelectAll: (checked: boolean) => void,
  selected: number[],
  updateSelected: (data: any) => void,
) => [
  {
    accessor: "caseId",
    Header: <Checkbox checked={selectAll} onClick={(e, { checked }) => setSelectAll(checked as boolean)} />,
    Cell: (data: any) => {
      return (
        <div className='tw-h-full tw-w-full tw-p-2.5' onClick={(e) => e.stopPropagation()} role='button' tabIndex={-1}>
          <Checkbox checked={selected.includes(data.value)} onClick={() => updateSelected(data.value)} />
        </div>
      );
    },
    width: 40,
    flexShrink: 0,
    style: {
      padding: 0,
    },
  },
  {
    id: "thumbnail",
    Header: <Icon name='camera' style={{ width: "100%", margin: "auto" }} />,
    Cell: (data: any) => {
      const hasAttachment = data.row.original.hasAttachment;
      const caseId = data.row.original.caseId;
      const isAttachmentLoading = crmRequestImages[caseId] === undefined;
      const isAttachmentImage = crmRequestImages[caseId] !== null;
      if (!hasAttachment) {
        return <></>;
      }
      if (isAttachmentLoading) {
        return (
          <Placeholder style={{ height: 90, width: 100 }}>
            <Placeholder.Image />
          </Placeholder>
        );
      }
      if (!isAttachmentImage) {
        return <Icon name='file outline' size='huge' color='grey' style={{ paddingTop: "15px", width: 100 }} />;
      }
      const thumbnail = (
        <div
          className='thumbnail-container'
          style={{
            background: `url(${crmRequestImages[caseId]}) no-repeat center`,
            backgroundSize: "cover",
          }}
        ></div>
      );
      const content = <img className='preview' alt={caseId} src={crmRequestImages[caseId]} />;
      return (
        <Popup pinned hoverable className='CrmListBody' trigger={thumbnail} content={content} position='right center' />
      );
    },
    width: 100,
    flexShrink: 0,
    style: {
      padding: 0,
      minWidth: "100px",
    },
  },
  {
    Header: "Status",
    accessor: "status",
    width: 100,
    style: {
      fontSize: ".9em",
    },
    Cell: ({ cell }: any) => {
      return (
        <Popup trigger={<span>{cell.value}</span>}>
          <Popup.Content>{cell.value}</Popup.Content>
        </Popup>
      );
    },
  },
  {
    Header: "Due",
    accessor: "dateExpectClose",
    Cell: (data: any) => {
      const dateClosed = data.row.original.dateClosed;
      if (dateClosed) {
        return <DateBadge value={data.cell.value} />;
      } else {
        return <DateDueBadge value={data.cell.value} />;
      }
    },
    width: 116,
    flexGrow: 0,
    flexShrink: 0,
    style: {
      textOverflow: "unset",
    },
  },
  {
    Header: "Topic+",
    Cell: (data: any) => {
      return (
        <div>
          <div className='tw-flex'>
            <TopicPlusHeader>{data.row.original.topicName}</TopicPlusHeader>
            <Label basic size='mini' className='tw-max-h-6'>
              {data.row.original.departmentName}
            </Label>
          </div>
          {data.row.original.location && data.row.original.location.length && (
            <TopicPlusLocation>{data.row.original.location}</TopicPlusLocation>
          )}
          <TopicPlusDescription>
            {_.toString(data.row.original.description).replace(/[\r\n]+/g, " ")}
          </TopicPlusDescription>
        </div>
      );
    },
    width: 400,
    flexGrow: 1,
    style: {
      padding: "0.5em",
    },
  },
  {
    Header: "Request #",
    accessor: "displayId",
    flexShrink: 0,
    width: 100,
    style: {
      fontSize: ".9em",
    },
    Cell: ({ cell }: any) => {
      const id = cell.row.original.caseId;
      const pageWhenDone = encodeURIComponent(window.location.href);
      const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${id}`;
      return (
        <Button
          icon
          size='mini'
          onClick={(e: any) => {
            e.stopPropagation();
            window.open(legacyUrl, "_blank");
          }}
        >
          {cell.value}
          <Icon name='external square alternate' size='large' />
        </Button>
      );
    },
  },
  {
    Header: "People",
    accessor: "contactDisplayName",
    Cell: ({ row }: any) => {
      return (
        <>
          <p>
            <Icon name='user' color='grey' />
            {row.original.contactDisplayName || "Anonymous"}
          </p>
          <p>
            <Icon name='clipboard' color='grey' />
            {row.original.assignedToDisplayName}
          </p>
        </>
      );
    },
    style: {
      fontSize: ".9em",
    },
  },
  {
    Header: "Updated",
    accessor: "dateLastUpdated",
    Cell: ({ cell }: any) => <DateTimeBadge value={cell.value} />,
    width: 115,
    flexGrow: 0,
    flexShrink: 0,
    sortDescFirst: true,
    style: {
      textOverflow: "unset",
    },
  },
  {
    Header: "Entered",
    accessor: "dateEntered",
    Cell: ({ cell }: any) => <DateTimeBadge value={cell.value} />,
    width: 115,
    height: 200,
    flexGrow: 0,
    flexShrink: 0,
    sortDescFirst: true,
    style: {
      textOverflow: "unset",
    },
  },
];
